import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { VECTOR_TILE_ROOT_URL } from '../../data-protocol-services/twc-vector-tile.data.service';
import {
  TwcResponseData,
  TwcUpdateData
} from '../../data-protocol-services/twc.data.types';
import { TWC_CONFIG_TOKEN } from '../../plugins/twc-config.token';
import {
  initialFlightLevel,
  initialTime,
  normalizeSelectedFlightLevel,
  normalizeSelectedTime
} from '../../twc.mixin';
import { TwcLayerServiceInterface } from '../../twc.refresh-layer-plugin.service';
import { TwcGeoJSONLayerData } from '../geo-json/twc-geo-json.layer.service';
import { TwcGraphicsLayer } from './twc-graphics.layer';
import { paramsObjectToArcgisUrlTemplate } from '../../twc-utils/url.utils';

export const FEATURE_ROOT_URL =
  'https://api.weather.com/v2/vector-api/products';

interface LayerParams {
  time: string;
  apiKey: string;
}

@Injectable({
  providedIn: 'root'
})
export class TwcGraphicsLayerService
  implements TwcLayerServiceInterface<TwcGraphicsLayer>
{
  private readonly env = inject(TWC_CONFIG_TOKEN);
  private readonly _httpClient = inject(HttpClient);
  private readonly _twcApiKey: string;

  readonly paramsObject: {
    format: string;
    lod: string;
    x: string;
    y: string;
    'tile-size': number;
    apiKey: string;
  };

  constructor() {
    if (!this.env?.apiKey) {
      throw new Error(
        'TWC API key is required but not found in environment configuration'
      );
    }
    this._twcApiKey = this.env.apiKey;

    this.paramsObject = {
      format: 'mvt',
      lod: '{z}',
      x: '{x}',
      y: '{y}',
      'tile-size': 256,
      apiKey: this._twcApiKey
    };
  }

  createLayer$(data: TwcResponseData): Observable<TwcGraphicsLayer> {
    if (!data?.twcProduct) {
      return throwError(() => new Error('TWC Product is required'));
    }

    const params: LayerParams = {
      time: initialTime(data.timeMap),
      apiKey: this._twcApiKey
    };

    const url = this.buildFeatureUrl(data.twcProduct.defaultProductId, params);

    // coverity[NULL_RETURNS:SUPPRESS]
    return this._httpClient.get<TwcGeoJSONLayerData>(url).pipe(
      map((geoJSONLayerData) => {
        if (!geoJSONLayerData?.features) {
          throw new Error('Invalid or empty GeoJSON data received');
        }
        if (!data.twcProduct.layerSpecs?.graphics?.convert2Graphics) {
          throw new Error('Graphics conversion function not found');
        }

        return new TwcGraphicsLayer({
          id: data.twcProduct.id,
          zOrder: data.twcProduct.zOrder,
          flightLevelValues: data.flightLevelValues ?? [],
          selectedFlightLevel: initialFlightLevel(data.flightLevelValues),
          timeMap: data.timeMap ?? {},
          label: data.twcProduct.label ?? 'Unknown',
          selectedTime: initialTime(data.timeMap),
          graphics: data.twcProduct.layerSpecs.graphics.convert2Graphics(
            geoJSONLayerData.features
          )
        });
      }),
      catchError((error) => {
        console.error('Error creating graphics layer:', error);
        return throwError(() => new Error('Failed to create graphics layer'));
      })
    );
  }

  updateLayer(
    layer: TwcGraphicsLayer,
    updateData: TwcUpdateData
  ): TwcGraphicsLayer {
    if (!layer) {
      throw new Error('Layer is required for update');
    }
    if (!updateData?.twcProduct) {
      throw new Error('TWC Product is required for update');
    }

    const selectedFlightLevel = normalizeSelectedFlightLevel(
      updateData.flightLevelValues,
      updateData.selectedFlightLevel,
      layer.selectedFlightLevel
    );
    const selectedTime = normalizeSelectedTime(
      updateData.timeMap,
      updateData.selectedTime,
      layer.selectedTime
    );

    // Update flight levels and time map
    layer.updateFlightLevels(updateData.flightLevelValues, selectedFlightLevel);
    layer.updateTimeMap(updateData.timeMap, selectedTime);

    const params: LayerParams = {
      time: selectedTime,
      apiKey: this._twcApiKey
    };

    const productId = this.getProductId(
      updateData.twcProduct.flightLevelProductIds,
      selectedFlightLevel,
      updateData.twcProduct.defaultProductId
    );

    const url = this.buildFeatureUrl(productId, params);

    // coverity[NULL_RETURNS:SUPPRESS]
    this._httpClient
      .get<TwcGeoJSONLayerData>(url)
      .pipe(
        map((geoJSONLayerData) => {
          if (!geoJSONLayerData?.features) {
            throw new Error('Invalid or empty GeoJSON data received');
          }
          if (!updateData.twcProduct.layerSpecs?.graphics?.convert2Graphics) {
            throw new Error('Graphics conversion function not found');
          }

          return updateData.twcProduct.layerSpecs.graphics.convert2Graphics(
            geoJSONLayerData.features
          );
        }),
        catchError((error) => {
          console.error('Error updating Graphics layer:', error);
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (graphics) => {
          if (graphics) {
            layer.removeAll();
            layer.set('graphics', graphics);
          }
        },
        error: (error) => {
          console.error('Error updating Graphics layer:', error);
        }
      });

    return layer;
  }

  private buildFeatureUrl(productId: string, params: LayerParams): string {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    return `${VECTOR_TILE_ROOT_URL}/${productId}/features?${paramsObjectToArcgisUrlTemplate(params)}`;
  }

  private getProductId(
    flightLevelProductIds: Record<string, string> | null,
    selectedFlightLevel: string | null,
    defaultProductId: string
  ): string {
    return (
      flightLevelProductIds?.[selectedFlightLevel ?? ''] ?? defaultProductId
    );
  }
}
